import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/ClientManage.module.css";
import modalStyles from "../css/css_pages/schedulesPopUp.module.css";

import {
  FiSearch,
  FiRefreshCw,
  FiX,
  FiFileText,
  FiMessageSquare,
  FiUser,
  FiClock,
  FiSend,
  FiCalendar,
} from "react-icons/fi";

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState(null);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modals
  const [showViewModal, setShowViewModal] = useState(false);

  // Ticket data
  const [currentTicket, setCurrentTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [commentAuthors, setCommentAuthors] = useState({});
  const [clients, setClients] = useState([]);
  const [tsos, setTsos] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [addCommentError, setAddCommentError] = useState(null);

  // Status and priority filters
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "assigned", label: "Assigned" },
    { value: "completed", label: "Completed" },
    { value: "confirmed", label: "Confirmed" },
    { value: "past_due", label: "Past Due" },
    { value: "closed", label: "Closed" },
  ];

  const priorityOptions = [
    { value: "all", label: "All Priority" },
    { value: "normal", label: "Normal" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  const typeOptions = [
    { value: "all", label: "All Types" },
    { value: "service", label: "Service" },
    { value: "installation", label: "Installation" },
    { value: "training", label: "Training" },
  ];

  // Fetch initial data
  useEffect(() => {
    fetchData();
    fetchClients();
    fetchTsoList();
  }, []);

  // Apply filters when they change
  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...tickets];

      if (searchTerm) {
        const term = searchTerm.toLowerCase().trim();
        filtered = filtered.filter((ticket) => {
          const ticketId = (ticket.ticket_id || "").toString().toLowerCase();
          const ticketNo = (ticket.ticket_no || "").toString().toLowerCase();
          const ticketIssue = (ticket.ticket_issue || "")
            .toString()
            .toLowerCase();
          const customerName = (ticket.customer_name || "")
            .toString()
            .toLowerCase();
          const customerId = (ticket.customer_id || "")
            .toString()
            .toLowerCase();
          const assignedTsoName = (ticket.assigned_tso_name || "")
            .toString()
            .toLowerCase();
          const phoneNumber = (ticket.ticket_contact_person_phone_number || "")
            .toString()
            .toLowerCase();

          return (
            ticketId.includes(term) ||
            ticketNo.includes(term) ||
            ticketIssue.includes(term) ||
            customerName.includes(term) ||
            customerId.includes(term) ||
            assignedTsoName.includes(term) ||
            phoneNumber.includes(term)
          );
        });
      }

      if (selectedStatusFilter && selectedStatusFilter.value !== "all") {
        filtered = filtered.filter((ticket) => {
          const ticketStatus = (ticket.ticket_status || "")
            .toString()
            .toLowerCase();
          const filterValue = selectedStatusFilter.value.toLowerCase();
          return ticketStatus === filterValue;
        });
      }

      if (selectedPriorityFilter && selectedPriorityFilter.value !== "all") {
        filtered = filtered.filter((ticket) => {
          const ticketPriority = (ticket.ticket_priority || "")
            .toString()
            .toLowerCase();
          const filterValue = selectedPriorityFilter.value.toLowerCase();
          return ticketPriority === filterValue;
        });
      }

      // TSO filter
      if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
        filtered = filtered.filter(
          (ticket) => ticket.assigned_tso_id === selectedTsoFilter.value
        );
      }

      // Date range filter
      if (startDate || endDate) {
        filtered = filtered.filter((ticket) => {
          if (!ticket.created_at) return false;

          const ticketDate = new Date(ticket.created_at);
          ticketDate.setHours(0, 0, 0, 0);

          if (startDate && endDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return ticketDate >= start && ticketDate <= end;
          } else if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            return ticketDate >= start;
          } else if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return ticketDate <= end;
          }
          return true;
        });
      }

      // Sort by created_at in descending order (newest first)
      filtered.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });

      setFilteredTickets(filtered);
    };

    applyFilters();
  }, [
    tickets,
    selectedStatusFilter,
    selectedPriorityFilter,
    selectedTsoFilter,
    startDate,
    endDate,
    searchTerm,
  ]);

  const fetchClients = async () => {
    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/management/get-all-clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.response) {
          setClients(data.response);
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchTsoList = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) return;

      const response = await fetch(`${config.API_URL}/management/tso-list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTsos(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching TSO list:", err);
    }
  };

  const [searchDebounce, setSearchDebounce] = useState(null);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    const newDebounce = setTimeout(() => {
      // Filter is automatically applied by useEffect
    }, 300);

    setSearchDebounce(newDebounce);
  };

  useEffect(() => {
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchDebounce]);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      // Fetch all tickets using the new non-paginated endpoint
      const ticketsResponse = await fetch(
        `${config.API_URL}/management/get-all-tickets`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!ticketsResponse.ok) throw new Error("Failed to fetch tickets");

      const ticketsData = await ticketsResponse.json();

      console.log("Tickets API Response:", ticketsData);
      console.log("Total tickets received:", ticketsData.total_count);

      if (ticketsData.success && ticketsData.tickets) {
        const ticketsList = ticketsData.tickets;

        console.log("Tickets array:", ticketsList);
        console.log("Number of tickets in array:", ticketsList.length);
        console.log("First ticket:", ticketsList[0]);
        console.log("Last ticket:", ticketsList[ticketsList.length - 1]);

        setTickets(Array.isArray(ticketsList) ? ticketsList : []);
        setFilteredTickets(Array.isArray(ticketsList) ? ticketsList : []);
      } else {
        console.log("No tickets found or API response unsuccessful");
        setTickets([]);
        setFilteredTickets([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchComments = async (ticketId) => {
    if (!ticketId) return;

    try {
      setIsLoadingComments(true);
      setCommentError(null);
      console.log("Tciket id", ticketId);
      const token = Cookies.get("authToken");
      console.log(ticketId);
      const response = await fetch(
        `${config.API_URL}/management/get-all-ticket-comments/${ticketId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch comments");

      const data = await response.json();
      console.log("Comments", data.response);

      if (Array.isArray(data.response)) {
        setComments(data.response);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setCommentError(error.message);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const fetchCommentAuthors = async (comments) => {
    try {
      const authors = {};

      for (const comment of comments) {
        const authorId = comment.commented_by;

        if (authors[authorId]) continue;

        // Check if it's a client
        const client = clients.find((c) => c.customer_id === authorId);
        if (client) {
          authors[authorId] = client.customer_name;
          continue;
        }

        // Check if it's a TSO
        const tso = tsos.find((t) => t.tso_id === authorId);
        if (tso) {
          authors[authorId] = tso.tso_calling_name;
          continue;
        }

        // If not found, use the ID
        authors[authorId] = authorId;
      }

      setCommentAuthors(authors);
    } catch (error) {
      console.error("Error fetching comment authors:", error);
      const authors = {};
      comments.forEach((comment) => {
        authors[comment.commented_by] = comment.commented_by;
      });
      setCommentAuthors(authors);
    }
  };

  const openViewModal = async (ticket) => {
    setCurrentTicket(ticket);
    setShowViewModal(true);
    await fetchComments(ticket.ticket_id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentTicket) return;

    setIsAddingComment(true);
    setAddCommentError(null);

    try {
      const token = Cookies.get("authToken");
      const userId = Cookies.get("userId");

      if (!token || !userId) {
        throw new Error("Authentication data not found");
      }

      console.log("Current Ticket", currentTicket);

      const response = await fetch(
        `${config.API_URL}/management/add-ticket-comment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            added_user: userId,
            ticket_id: currentTicket.ticket_id,
            comment: newComment.trim(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to add comment");
      }

      // Refresh comments after successful addition
      await fetchComments(currentTicket.ticket_id);
      setNewComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
      setAddCommentError(error.message);
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleFilterChange = (selected, filterType) => {
    if (filterType === "status") {
      setSelectedStatusFilter(selected);
    } else if (filterType === "priority") {
      setSelectedPriorityFilter(selected);
    } else if (filterType === "tso") {
      setSelectedTsoFilter(selected);
    }
  };

  const clearFilters = () => {
    setSelectedStatusFilter(null);
    setSelectedPriorityFilter(null);
    setSelectedTsoFilter(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case "normal":
        return styles.priorityNormal;
      case "medium":
        return styles.priorityMedium;
      case "high":
        return styles.priorityHigh;
      default:
        return "";
    }
  };

  const getStatusClass = (status) => {
    if (!status) return "";
    const statusStr = String(status).toLowerCase();
    switch (statusStr) {
      case "pending":
        return styles.statusPending;
      case "in_progress":
        return styles.statusInProgress;
      case "assigned":
        return styles.statusAssigned;
      case "completed":
        return styles.statusCompleted;
      case "confirmed":
        return styles.statusConfirmed;
      case "past_due":
        return styles.statusPastDue;
      case "closed":
        return styles.statusClosed;
      default:
        return "";
    }
  };

  const getPriorityLabel = (priority) => {
    if (!priority) return "N/A";
    const priorityStr = String(priority).toLowerCase();
    switch (priorityStr) {
      case "normal":
        return "Normal";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      default:
        return priority;
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return "N/A";
    const statusStr = String(status).toLowerCase();
    switch (statusStr) {
      case "pending":
        return "Pending";
      case "in_progress":
        return "In Progress";
      case "assigned":
        return "Assigned";
      case "completed":
        return "Completed";
      case "confirmed":
        return "Confirmed";
      case "past_due":
        return "Past Due";
      case "closed":
        return "Closed";
      default:
        return status || "Not Available";
    }
  };

  const getTypeLabel = (type) => {
    if (!type) return "Not Available";
    const typeStr = String(type).toLowerCase();
    switch (typeStr) {
      case "not-working":
      case "not_working":
        return "Not Working";
      case "data-correction":
      case "data_correction":
        return "Data Correction";
      case "service":
        return "Service";
      case "installation":
        return "Installation";
      case "training":
        return "Training";
      default:
        // Handle any other formats by removing underscores/hyphens and capitalizing
        return type
          .toString()
          .replace(/[-_]/g, " ")
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ");
    }
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading tickets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Ticket Management</h1>
          <button
            className={styles.refreshButton}
            disabled={isRefreshing}
            onClick={fetchData}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.filterSectionHeader}>
          <h3 className={styles.filterSectionTitle}>Quick Filters</h3>
          <div className={styles.quickFilterButtons}>
            <button
              className={styles.quickFilterBtn}
              onClick={() => {
                const today = new Date();
                setStartDate(today);
                setEndDate(today);
              }}
            >
              Today
            </button>
            <button
              className={styles.quickFilterBtn}
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            >
              All
            </button>
            <button
              className={`${styles.quickFilterBtn} ${styles.clearBtn}`}
              onClick={clearFilters}
            >
              Clear All
            </button>
          </div>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.filterGrid}>
            <Select
              options={statusOptions}
              value={selectedStatusFilter}
              onChange={(selected) => handleFilterChange(selected, "status")}
              styles={customSelectStyles}
              placeholder="Status"
              isClearable
              className={styles.filterSelect}
            />
            <Select
              options={priorityOptions}
              value={selectedPriorityFilter}
              onChange={(selected) => handleFilterChange(selected, "priority")}
              styles={customSelectStyles}
              placeholder="Priority"
              isClearable
              className={styles.filterSelect}
            />
            <Select
              options={[
                { value: "all", label: "All TSOs" },
                ...tsos.map((tso) => ({
                  value: tso.tso_id,
                  label: tso.tso_calling_name || tso.tso_name,
                })),
              ]}
              value={selectedTsoFilter}
              onChange={(selected) => handleFilterChange(selected, "tso")}
              styles={customSelectStyles}
              placeholder="TSO"
              isClearable
              className={styles.filterSelect}
            />
            <div className={styles.datePickerWrapper}>
              <FiCalendar className={styles.calendarIcon} />
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start"
                dateFormat="MMM dd"
                className={styles.datePicker}
                isClearable
              />
            </div>
            <div className={styles.datePickerWrapper}>
              <FiCalendar className={styles.calendarIcon} />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End"
                dateFormat="MMM dd"
                className={styles.datePicker}
                isClearable
              />
            </div>
            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredTickets.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                <div>
                  Showing {filteredTickets.length} ticket
                  {filteredTickets.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>Ticket ID</th>
                      <th style={{ width: "140px" }}>Type</th>
                      <th style={{ width: "140px" }}>Priority</th>
                      <th style={{ width: "150px" }}>Client Name</th>
                      <th style={{ width: "120px" }}>Client ID</th>
                      <th style={{ width: "130px" }}>Contact</th>
                      <th style={{ minWidth: "200px" }}>Issue</th>
                      <th style={{ width: "130px" }}>TSO</th>
                      <th style={{ width: "120px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket, index) => (
                      <tr
                        key={`${ticket.ticket_no}-${index}`}
                        className={styles.clickableRow}
                        onClick={() => openViewModal(ticket)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{ticket.ticket_no || "Not Available"}</td>
                        <td>{getTypeLabel(ticket.ticket_type)}</td>
                        <td>
                          <span
                            className={`${
                              styles.priorityBadge
                            } ${getPriorityClass(ticket.ticket_priority)}`}
                          >
                            {getPriorityLabel(ticket.ticket_priority)}
                          </span>
                        </td>
                        <td>{ticket.customer_name || "Not Available"}</td>
                        <td>{ticket.customer_id || "Not Available"}</td>
                        <td>
                          {ticket.ticket_contact_person_phone_number ||
                            "Not Available"}
                        </td>
                        <td>
                          <div
                            className={styles.issueText}
                            title={ticket.ticket_issue}
                          >
                            {ticket.ticket_issue
                              ? ticket.ticket_issue.length > 50
                                ? `${ticket.ticket_issue.substring(0, 50)}...`
                                : ticket.ticket_issue
                              : "Not Available"}
                          </div>
                        </td>
                        <td>{ticket.assigned_tso_name || "Unassigned"}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${getStatusClass(
                              ticket.ticket_status
                            )}`}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {getStatusLabel(ticket.ticket_status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>No tickets found matching your criteria.</p>
              {(selectedStatusFilter ||
                selectedPriorityFilter ||
                selectedTsoFilter ||
                startDate ||
                endDate ||
                searchTerm) && (
                <button className={styles.clearFilters} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Ticket Modal */}
      {showViewModal && currentTicket && (
        <div className={modalStyles.overlay}>
          <div className={modalStyles.content}>
            <div className={modalStyles.header}>
              <h2>Ticket Details ({currentTicket.ticket_no})</h2>
              <button
                className={modalStyles.closeBtn}
                onClick={() => setShowViewModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={modalStyles.body}>
              <div className={modalStyles.section}>
                <div className={modalStyles.grid}>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Ticket Number:</span>
                    <span className={modalStyles.value}>
                      {currentTicket.ticket_no || "N/A"}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Type:</span>
                    <span className={modalStyles.value}>
                      {getTypeLabel(currentTicket.ticket_type)}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Priority:</span>
                    <span
                      className={`${modalStyles.value} ${
                        modalStyles.statusBadge
                      } ${getPriorityClass(currentTicket.ticket_priority)}`}
                    >
                      {getPriorityLabel(currentTicket.ticket_priority)}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Status:</span>
                    <span
                      className={`${modalStyles.value} ${
                        modalStyles.statusBadge
                      } ${getStatusClass(currentTicket.ticket_status)}`}
                    >
                      {getStatusLabel(currentTicket.ticket_status)}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Customer:</span>
                    <span className={modalStyles.value}>
                      {currentTicket.customer_name || "N/A"}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Contact Number:</span>
                    <span className={modalStyles.value}>
                      {currentTicket.ticket_contact_person_phone_number ||
                        "N/A"}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Assigned TSO:</span>
                    <span className={modalStyles.value}>
                      {currentTicket.assigned_tso_name || "Unassigned"}
                    </span>
                  </div>
                  <div className={modalStyles.item}>
                    <span className={modalStyles.label}>Schedule ID:</span>
                    <span className={modalStyles.value}>
                      {currentTicket.assigned_schedule_id || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={modalStyles.section}>
                <h3 className={modalStyles.sectionTitle}>
                  <FiFileText /> Issue Description
                </h3>
                <div className={modalStyles.item}>
                  <span className={modalStyles.value}>
                    {currentTicket.ticket_issue || "N/A"}
                  </span>
                </div>
              </div>

              {/* Comments Section */}
              <div className={modalStyles.section}>
                <h3 className={modalStyles.sectionTitle}>
                  <FiMessageSquare /> Comments ({comments.length})
                </h3>

                {isLoadingComments ? (
                  <div className={modalStyles.loadingComments}>
                    <p>Loading comments...</p>
                  </div>
                ) : commentError ? (
                  <div className={modalStyles.commentsError}>
                    <p>Error loading comments: {commentError}</p>
                  </div>
                ) : comments.length > 0 ? (
                  <div className={modalStyles.commentsContainer}>
                    {comments.map((comment) => (
                      <div
                        key={comment.comment_id}
                        className={modalStyles.comment}
                      >
                        <div className={modalStyles.commentHeader}>
                          <span className={modalStyles.commentAuthor}>
                            <FiUser /> {comment.commented_by || "Unknown User"}
                          </span>
                          <span className={modalStyles.commentDate}>
                            <FiClock /> {formatDateTime(comment.commented_date)}
                          </span>
                        </div>
                        <div className={modalStyles.commentText}>
                          {comment.comment || "No comment text"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={modalStyles.noComments}>
                    <p>No comments yet</p>
                  </div>
                )}

                {/* Add Comment Section */}
                <div className={modalStyles.addCommentSection}>
                  <div className={modalStyles.commentInputContainer}>
                    <textarea
                      className={modalStyles.commentInput}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={isAddingComment}
                    />
                    <button
                      className={modalStyles.addCommentButton}
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isAddingComment}
                    >
                      {isAddingComment ? (
                        "Posting..."
                      ) : (
                        <>
                          <FiSend /> Post
                        </>
                      )}
                    </button>
                  </div>
                  {addCommentError && (
                    <div className={modalStyles.commentError}>
                      {addCommentError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom styles for react-select component
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "42px",
    padding: "0 12px",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    boxShadow: "none",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    fontSize: "0.875rem",
    color: "#6b7280",
    "&:hover": {
      borderColor: "#9ca3af",
    },
    "&:focus": {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  }),
  input: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "#6b7280",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
      ? "#f3f4f6"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    fontSize: "0.875rem",
    padding: "0.75rem 1rem",
    "&:hover": {
      backgroundColor: state.isSelected ? "#3b82f6" : "#f3f4f6",
      color: state.isSelected ? "white" : "#111827",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0.5rem 0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    padding: "0 8px",
    "&:hover": {
      color: "#6b7280",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "0.875rem",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#6b7280",
    fontSize: "0.875rem",
  }),
};

export default Tickets;
